import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { apiService } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  Plus, 
  Users, 
  Clock, 
  MapPin, 
  Flame,
  ChevronRight,
  Loader2,
  Tags,
  Filter,
  X
} from 'lucide-react';
import { toast } from 'sonner';

export const CelulasPage = () => {
  const { celulas, loadingCelulas, createCelula, igrejaAtual, loadCelulas } = useApp();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // Category filter state
  const [categorias, setCategorias] = useState([]);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [selectedCategoriaId, setSelectedCategoriaId] = useState('all');
  
  const [newCelula, setNewCelula] = useState({
    nome: '',
    descricao: '',
    objetivo: '',
    endereco: '',
    cidade: '',
    dia_semana: '',
    horario: '',
    principios: [],
    metas: [],
  });

  // Load categories when church changes
  useEffect(() => {
    const fetchCategorias = async () => {
      if (!igrejaAtual?.id) return;
      
      try {
        setLoadingCategorias(true);
        const data = await apiService.getCategorias(igrejaAtual.id);
        setCategorias(data);
      } catch (error) {
        console.error('Error loading categorias:', error);
      } finally {
        setLoadingCategorias(false);
      }
    };
    
    fetchCategorias();
  }, [igrejaAtual?.id]);

  // Handle category filter change
  const handleCategoriaFilter = async (value) => {
    setSelectedCategoriaId(value);
    
    if (!igrejaAtual?.id) {
      console.warn('No igreja selected');
      return;
    }
    
    try {
      if (value === 'all' || !value) {
        // Quando seleciona "Todas", passa null para não filtrar
        await loadCelulas(igrejaAtual.id, null);
      } else {
        await loadCelulas(igrejaAtual.id, [value]);
      }
    } catch (error) {
      console.error('Error filtering celulas:', error);
      toast.error('Erro ao filtrar células');
    }
  };

  const filteredCelulas = celulas.filter(celula =>
    celula.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (celula.descricao && celula.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCreateCelula = async () => {
    if (!newCelula.nome.trim()) return;
    
    setIsCreating(true);
    try {
      await createCelula({
        ...newCelula,
        principios: newCelula.principios.length > 0 ? newCelula.principios : [],
        metas: newCelula.metas.length > 0 ? newCelula.metas : [],
      });
      setIsDialogOpen(false);
      setNewCelula({
        nome: '',
        descricao: '',
        objetivo: '',
        endereco: '',
        cidade: '',
        dia_semana: '',
        horario: '',
        principios: [],
        metas: [],
      });
    } catch (error) {
      console.error('Error creating celula:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const diasSemana = [
    'Segunda-feira',
    'Terça-feira',
    'Quarta-feira',
    'Quinta-feira',
    'Sexta-feira',
    'Sábado',
    'Domingo'
  ];

  return (
    <div className="page-transition">
      {/* Header */}
      <div className="px-6 lg:px-8 py-8 bg-gradient-to-r from-primary/10 via-background to-secondary/10 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary" className="text-xs">
                  {igrejaAtual?.nome || 'Igreja'}
                </Badge>
              </div>
              <h1 className="font-heading text-3xl lg:text-4xl font-bold text-foreground">
                Células
              </h1>
              <p className="text-muted-foreground mt-1">
                {loadingCelulas ? 'Carregando...' : `${celulas.length} células cadastradas`}
              </p>
            </div>
            
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button disabled={!igrejaAtual}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Célula
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Criar Nova Célula</DialogTitle>
                  <DialogDescription>
                    Preencha as informações da nova célula
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="nome">Nome da Célula *</Label>
                    <Input
                      id="nome"
                      placeholder="Ex: Célula Família em Cristo"
                      value={newCelula.nome}
                      onChange={(e) => setNewCelula({ ...newCelula, nome: e.target.value })}
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="descricao">Descrição</Label>
                    <Textarea
                      id="descricao"
                      placeholder="Descreva a célula..."
                      value={newCelula.descricao}
                      onChange={(e) => setNewCelula({ ...newCelula, descricao: e.target.value })}
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="objetivo">Objetivo</Label>
                    <Input
                      id="objetivo"
                      placeholder="Qual o objetivo principal da célula?"
                      value={newCelula.objetivo}
                      onChange={(e) => setNewCelula({ ...newCelula, objetivo: e.target.value })}
                    />
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="dia_semana">Dia da Semana</Label>
                      <Select
                        value={newCelula.dia_semana}
                        onValueChange={(value) => setNewCelula({ ...newCelula, dia_semana: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o dia" />
                        </SelectTrigger>
                        <SelectContent>
                          {diasSemana.map(dia => (
                            <SelectItem key={dia} value={dia}>{dia}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid gap-2">
                      <Label htmlFor="horario">Horário</Label>
                      <Input
                        id="horario"
                        type="time"
                        value={newCelula.horario}
                        onChange={(e) => setNewCelula({ ...newCelula, horario: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="endereco">Endereço</Label>
                      <Input
                        id="endereco"
                        placeholder="Rua, número"
                        value={newCelula.endereco}
                        onChange={(e) => setNewCelula({ ...newCelula, endereco: e.target.value })}
                      />
                    </div>
                    
                    <div className="grid gap-2">
                      <Label htmlFor="cidade">Cidade</Label>
                      <Input
                        id="cidade"
                        placeholder="Cidade"
                        value={newCelula.cidade}
                        onChange={(e) => setNewCelula({ ...newCelula, cidade: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleCreateCelula} disabled={!newCelula.nome.trim() || isCreating}>
                    {isCreating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Criando...
                      </>
                    ) : (
                      'Criar Célula'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar células..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-celulas-input"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Filter className="w-4 h-4" />
                <span className="text-sm hidden sm:inline">Categoria:</span>
              </div>
              <Select
                value={selectedCategoriaId}
                onValueChange={handleCategoriaFilter}
                disabled={loadingCategorias}
              >
                <SelectTrigger className="w-[180px]" data-testid="categoria-filter-select">
                  <SelectValue placeholder="Filtrar por categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categorias.map((categoria) => (
                    <SelectItem key={categoria.id} value={categoria.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: categoria.cor }}
                        />
                        {categoria.nome}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedCategoriaId !== 'all' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleCategoriaFilter('all')}
                  className="h-8 w-8"
                  data-testid="clear-filter-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Grid */}
          {loadingCelulas ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Card key={i} className="card-elevated">
                  <CardContent className="p-6">
                    <Skeleton className="h-12 w-12 rounded-xl mb-4" />
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-full mb-4" />
                    <Skeleton className="h-4 w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredCelulas.length === 0 ? (
            <Card className="card-elevated">
              <CardContent className="p-12 text-center">
                <Flame className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-xl mb-2">
                  {searchTerm ? 'Nenhuma célula encontrada' : 'Nenhuma célula cadastrada'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchTerm 
                    ? 'Tente buscar com outros termos'
                    : 'Comece criando sua primeira célula!'}
                </p>
                {!searchTerm && (
                  <Button onClick={() => setIsDialogOpen(true)} disabled={!igrejaAtual}>
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Primeira Célula
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCelulas.map((celula, index) => (
                <Link key={celula.id} to={`/celulas/${celula.id}`}>
                  <Card 
                    className="card-elevated hover:border-primary/50 transition-all duration-300 h-full animate-scale-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-3 rounded-xl bg-primary/10">
                          <Flame className="w-6 h-6 text-primary" />
                        </div>
                        <Badge variant="secondary">
                          {celula.dia_semana || 'A definir'}
                        </Badge>
                      </div>
                      
                      <h3 className="font-semibold text-lg text-foreground mb-2">
                        {celula.nome}
                      </h3>
                      
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {celula.descricao || 'Sem descrição'}
                      </p>
                      
                      <div className="space-y-2 text-sm text-muted-foreground">
                        {celula.horario && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{celula.horario}</span>
                          </div>
                        )}
                        {celula.cidade && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{celula.cidade}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          Ver detalhes
                        </span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
